import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import MenuIcon from "@mui/icons-material/Menu";
import { Paper, useMediaQuery } from "@mui/material";
import { Close, Description, Work, ContactMail } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { AnimatedButton } from "../ui/AnimatedButton";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";


const navRoles = ["Front-End Developer", "UI/UX Designer", "Web Developer"];

export default function Header() {
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const section = document.querySelector(location.hash);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);
  

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const springConfig = { stiffness: 120, damping: 30, restDelta: 0.001 };

  const y = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 150]),
    springConfig
  );

  const opacity = useSpring(
    useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.5, 0]),
    springConfig
  );

  const scale = useSpring(
    useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.9, 0.8]),
    springConfig
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRoleIndex((prevIndex) => (prevIndex + 1) % navRoles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isDesktop && modalOpen) {
      setModalOpen(false);
    }
  }, [isDesktop, modalOpen]);

  const handleModalToggle = () => {
    setModalOpen((prevState) => !prevState);
  };

  const navItems = [
    { title: "Projects", icon: <Work fontSize="small" />, to: "/#projects" },
    { title: "About", icon: <Description fontSize="small" />, to: "/about" },
    {
      title: "Contact",
      icon: <ContactMail fontSize="small" />,
      to: "/#contact",
    },
    { title: "Close", icon: <Close fontSize="small" />, to: "", click: handleModalToggle },
  ];

  return (
    <Box ref={containerRef}>
      <motion.div style={{ y, opacity, scale }}>
        <AppBar position="static">
          <Toolbar>
            {/* Logo and Role Section */}
            <motion.div
              initial={{ y: "-100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ duration: 0.6 }}
              style={{ display: "flex", alignItems: "center", flexGrow: 1 }}
            >
              <Box
                component={NavLink}
                to="/"
                sx={{
                  opacity: 1,
                  transition: "opacity 0.3s ease",
                  "&:hover": {
                    opacity: 0.5,
                  },
                }}
              >
                {/* Clickable logo text only */}
                <Typography
                  variant="body1"
                  sx={{
                    background: "rgba(255, 255, 255, 0.9)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    color: "transparent",
                    fontFamily: "'Futura PT', sans-serif",
                    textDecoration: "none",
                    lineHeight: 1,
                  }}
                  gutterBottom
                >
                  Abdulrahman Othman
                </Typography>

                {/* Animated Role Title Section (not clickable) */}
                <Box
                  sx={{
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: "20px",
                    position: "relative",
                  }}
                >
                  <motion.div
                    key={currentRoleIndex}
                    initial={{ y: "100%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: "-100%", opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    style={{ position: "absolute" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {navRoles[currentRoleIndex]}
                    </Typography>
                  </motion.div>
                </Box>
              </Box>
            </motion.div>

            {/* Desktop Menu */}
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
              {navItems.slice(0, 3).map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                >
                  <Link to={item.to} viewTransition>
                    <AnimatedButton
                      sx={{ fontSize: 15, height: "2.6rem", width: "6rem" }}
                    >
                      {item.title}
                    </AnimatedButton>
                  </Link>
                </motion.div>
              ))}
            </Box>

            {/* Mobile Menu Icon */}
            <AnimatedButton
              onClick={handleModalToggle}
              sx={{
                display: { xs: "flex", md: "none" },
                height: "2.8rem",
                width: "3rem",
                minWidth: 0,
              }}
            >
              <MenuIcon fontSize="small" />
            </AnimatedButton>
          </Toolbar>
        </AppBar>
      </motion.div>

      {/* Mobile Modal */}
      <Modal
        open={modalOpen}
        onClose={handleModalToggle}
        aria-labelledby="mobile-navigation"
        aria-describedby="mobile-menu-navigation"
        sx={{ display: { xs: "block", md: "none" } }}
      >
        <Paper
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            position: "absolute",
            bottom: 25,
            left: "50%",
            transform: "translate(-50%, 0)",
            p: 2.5,
            borderRadius: 5,
          }}
        >
          {/* Menu Items */}
          {navItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
            >
              <Link to={item.to}>
                <AnimatedButton
                  toolTip={item.title}
                  onClick={item.click}
                  sx={{ height: "2.8rem", width: "3rem", minWidth: 0 }}
                >
                  {item.icon}
                </AnimatedButton>
              </Link>
            </motion.div>
          ))}
        </Paper>
      </Modal>
    </Box>
  );
}
